import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { 
  Palette, 
  Upload, 
  Eye, 
  Save,
  RotateCcw,
  Brush,
  Image as ImageIcon,
  Type,
  Layout,
  Zap
} from "lucide-react";

const ThemeCustomizer = ({ settings, onSettingsChange }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  
  const [themeSettings, setThemeSettings] = useState({
    companyName: settings.companyName || "CardMaster Pro",
    logo: settings.logo || "",
    logoFile: null,
    primaryColor: settings.primaryColor || "#10b981",
    secondaryColor: settings.secondaryColor || "#3b82f6",
    accentColor: settings.accentColor || "#8b5cf6",
    backgroundColor: settings.backgroundColor || "#ffffff",
    textColor: settings.textColor || "#1f2937",
    headerStyle: settings.headerStyle || "modern",
    fontFamily: settings.fontFamily || "Inter",
    borderRadius: settings.borderRadius || "0.5rem",
    footerText: settings.footerText || "",
    customCSS: settings.customCSS || ""
  });

  const colorPresets = [
    { name: "Emerald Professional", primary: "#10b981", secondary: "#3b82f6", accent: "#8b5cf6" },
    { name: "Ocean Blue", primary: "#0ea5e9", secondary: "#3b82f6", accent: "#06b6d4" },
    { name: "Purple Gradient", primary: "#8b5cf6", secondary: "#a855f7", accent: "#ec4899" },
    { name: "Warm Orange", primary: "#f97316", secondary: "#ea580c", accent: "#dc2626" },
    { name: "Forest Green", primary: "#059669", secondary: "#047857", accent: "#065f46" },
    { name: "Midnight Dark", primary: "#1f2937", secondary: "#374151", accent: "#6b7280" }
  ];

  const headerStyles = [
    { value: "modern", name: "Modern Clean" },
    { value: "gradient", name: "Gradient Background" },
    { value: "glassmorphism", name: "Glassmorphism" },
    { value: "minimal", name: "Minimal" },
    { value: "corporate", name: "Corporate" }
  ];

  const fontOptions = [
    { value: "Inter", name: "Inter (Modern)" },
    { value: "Poppins", name: "Poppins (Friendly)" },
    { value: "Roboto", name: "Roboto (Clean)" },
    { value: "Open Sans", name: "Open Sans (Professional)" },
    { value: "Montserrat", name: "Montserrat (Elegant)" }
  ];

  const handleThemeSettingChange = (key, value) => {
    setThemeSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 2MB",
        variant: "destructive"
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setThemeSettings(prev => ({
        ...prev,
        logo: e.target.result,
        logoFile: file
      }));
    };
    reader.readAsDataURL(file);
  };

  const applyColorPreset = (preset) => {
    setThemeSettings(prev => ({
      ...prev,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      accentColor: preset.accent
    }));
    toast({
      title: "Color preset applied",
      description: `Applied ${preset.name} color scheme`
    });
  };

  const saveThemeSettings = async () => {
    setLoading(true);
    try {
      // Mock API call to save theme settings
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Apply settings to parent component
      Object.keys(themeSettings).forEach(key => {
        onSettingsChange(key, themeSettings[key]);
      });

      toast({
        title: "Theme saved successfully",
        description: "Your customizations have been applied to the system"
      });
    } catch (error) {
      toast({
        title: "Save failed",
        description: "Failed to save theme settings. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = () => {
    setThemeSettings({
      companyName: "CardMaster Pro",
      logo: "",
      logoFile: null,
      primaryColor: "#10b981",
      secondaryColor: "#3b82f6",
      accentColor: "#8b5cf6",
      backgroundColor: "#ffffff",
      textColor: "#1f2937",
      headerStyle: "modern",
      fontFamily: "Inter",
      borderRadius: "0.5rem",
      footerText: "",
      customCSS: ""
    });
    toast({
      title: "Reset to defaults",
      description: "All theme settings have been reset to default values"
    });
  };

  // Live preview styles
  const previewStyles = {
    '--primary-color': themeSettings.primaryColor,
    '--secondary-color': themeSettings.secondaryColor,
    '--accent-color': themeSettings.accentColor,
    '--background-color': themeSettings.backgroundColor,
    '--text-color': themeSettings.textColor,
    '--border-radius': themeSettings.borderRadius,
    '--font-family': themeSettings.fontFamily
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Palette className="h-6 w-6 text-purple-600" />
              <span>Theme & Brand Customization</span>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                onClick={() => setPreviewMode(!previewMode)}
                className="flex items-center space-x-2"
              >
                <Eye className="h-4 w-4" />
                <span>{previewMode ? "Edit Mode" : "Preview"}</span>
              </Button>
              <Button variant="outline" onClick={resetToDefaults}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {previewMode ? (
            // Live Preview
            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div 
                  className="p-6 text-white"
                  style={{
                    background: `linear-gradient(135deg, ${themeSettings.primaryColor}, ${themeSettings.secondaryColor})`,
                    fontFamily: themeSettings.fontFamily
                  }}
                >
                  <div className="flex items-center space-x-3">
                    {themeSettings.logo && (
                      <img src={themeSettings.logo} alt="Logo" className="w-10 h-10 object-cover rounded" />
                    )}
                    <div>
                      <h1 className="text-2xl font-bold">{themeSettings.companyName}</h1>
                      <p className="opacity-90">Professional Business Cards & MLM Platform</p>
                    </div>
                  </div>
                </div>
                
                <div 
                  className="p-6"
                  style={{
                    backgroundColor: themeSettings.backgroundColor,
                    color: themeSettings.textColor,
                    fontFamily: themeSettings.fontFamily
                  }}
                >
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div 
                      className="p-4 rounded text-white text-center"
                      style={{ 
                        backgroundColor: themeSettings.primaryColor,
                        borderRadius: themeSettings.borderRadius
                      }}
                    >
                      <h3 className="font-semibold mb-2">Primary Color</h3>
                      <p className="text-sm opacity-90">Main brand color</p>
                    </div>
                    <div 
                      className="p-4 rounded text-white text-center"
                      style={{ 
                        backgroundColor: themeSettings.secondaryColor,
                        borderRadius: themeSettings.borderRadius
                      }}
                    >
                      <h3 className="font-semibold mb-2">Secondary Color</h3>
                      <p className="text-sm opacity-90">Complementary color</p>
                    </div>
                    <div 
                      className="p-4 rounded text-white text-center"
                      style={{ 
                        backgroundColor: themeSettings.accentColor,
                        borderRadius: themeSettings.borderRadius
                      }}
                    >
                      <h3 className="font-semibold mb-2">Accent Color</h3>
                      <p className="text-sm opacity-90">Highlight color</p>
                    </div>
                  </div>

                  <div className="text-center">
                    <button 
                      className="px-6 py-3 rounded font-semibold text-white"
                      style={{
                        background: `linear-gradient(135deg, ${themeSettings.primaryColor}, ${themeSettings.secondaryColor})`,
                        borderRadius: themeSettings.borderRadius
                      }}
                    >
                      Sample Call-to-Action Button
                    </button>
                  </div>

                  {themeSettings.footerText && (
                    <div className="mt-6 pt-4 border-t text-center text-sm opacity-70">
                      {themeSettings.footerText}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Theme Editor
            <div className="space-y-8">
              {/* Brand Identity */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                  <Brush className="h-5 w-5 text-blue-600" />
                  <span>Brand Identity</span>
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Company Name</Label>
                      <Input
                        value={themeSettings.companyName}
                        onChange={(e) => handleThemeSettingChange("companyName", e.target.value)}
                        placeholder="Your Company Name"
                      />
                    </div>

                    <div>
                      <Label>Logo Upload</Label>
                      <div className="flex items-center space-x-4">
                        <Button
                          variant="outline"
                          onClick={() => document.getElementById('logo-upload').click()}
                          className="flex items-center space-x-2"
                        >
                          <Upload className="h-4 w-4" />
                          <span>Upload Logo</span>
                        </Button>
                        {themeSettings.logo && (
                          <img 
                            src={themeSettings.logo} 
                            alt="Logo preview" 
                            className="w-12 h-12 object-cover rounded border"
                          />
                        )}
                      </div>
                      <input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label>Header Style</Label>
                      <Select 
                        value={themeSettings.headerStyle} 
                        onValueChange={(value) => handleThemeSettingChange("headerStyle", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {headerStyles.map((style) => (
                            <SelectItem key={style.value} value={style.value}>
                              {style.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Font Family</Label>
                      <Select 
                        value={themeSettings.fontFamily} 
                        onValueChange={(value) => handleThemeSettingChange("fontFamily", value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fontOptions.map((font) => (
                            <SelectItem key={font.value} value={font.value}>
                              {font.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Color Customization */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                  <Palette className="h-5 w-5 text-purple-600" />
                  <span>Color Scheme</span>
                </h3>

                {/* Color Presets */}
                <div className="mb-6">
                  <Label className="mb-3 block">Quick Color Presets</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {colorPresets.map((preset, index) => (
                      <button
                        key={index}
                        onClick={() => applyColorPreset(preset)}
                        className="p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                      >
                        <div className="flex space-x-2 mb-2">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: preset.primary }}
                          ></div>
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: preset.secondary }}
                          ></div>
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: preset.accent }}
                          ></div>
                        </div>
                        <p className="text-sm font-medium">{preset.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Colors */}
                <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <Label>Primary Color</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      <input
                        type="color"
                        value={themeSettings.primaryColor}
                        onChange={(e) => handleThemeSettingChange("primaryColor", e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={themeSettings.primaryColor}
                        onChange={(e) => handleThemeSettingChange("primaryColor", e.target.value)}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Secondary Color</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      <input
                        type="color"
                        value={themeSettings.secondaryColor}
                        onChange={(e) => handleThemeSettingChange("secondaryColor", e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={themeSettings.secondaryColor}
                        onChange={(e) => handleThemeSettingChange("secondaryColor", e.target.value)}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Accent Color</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      <input
                        type="color"
                        value={themeSettings.accentColor}
                        onChange={(e) => handleThemeSettingChange("accentColor", e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={themeSettings.accentColor}
                        onChange={(e) => handleThemeSettingChange("accentColor", e.target.value)}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Background Color</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      <input
                        type="color"
                        value={themeSettings.backgroundColor}
                        onChange={(e) => handleThemeSettingChange("backgroundColor", e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={themeSettings.backgroundColor}
                        onChange={(e) => handleThemeSettingChange("backgroundColor", e.target.value)}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Text Color</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      <input
                        type="color"
                        value={themeSettings.textColor}
                        onChange={(e) => handleThemeSettingChange("textColor", e.target.value)}
                        className="w-10 h-10 rounded border cursor-pointer"
                      />
                      <Input
                        value={themeSettings.textColor}
                        onChange={(e) => handleThemeSettingChange("textColor", e.target.value)}
                        className="font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced Customization */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
                  <Layout className="h-5 w-5 text-green-600" />
                  <span>Advanced Customization</span>
                </h3>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Footer Text</Label>
                    <Textarea
                      value={themeSettings.footerText}
                      onChange={(e) => handleThemeSettingChange("footerText", e.target.value)}
                      placeholder="© 2024 Your Company. All rights reserved."
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label>Border Radius</Label>
                    <Select 
                      value={themeSettings.borderRadius} 
                      onValueChange={(value) => handleThemeSettingChange("borderRadius", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Sharp (0px)</SelectItem>
                        <SelectItem value="0.25rem">Small (4px)</SelectItem>
                        <SelectItem value="0.5rem">Medium (8px)</SelectItem>
                        <SelectItem value="0.75rem">Large (12px)</SelectItem>
                        <SelectItem value="1rem">Extra Large (16px)</SelectItem>
                        <SelectItem value="9999px">Fully Rounded</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Custom CSS (Advanced Users)</Label>
                  <Textarea
                    value={themeSettings.customCSS}
                    onChange={(e) => handleThemeSettingChange("customCSS", e.target.value)}
                    placeholder="/* Add your custom CSS here */"
                    rows={6}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Add custom CSS to override default styles. Use with caution.
                  </p>
                </div>
              </div>

              {/* Credits Section */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg">
                <div className="text-center">
                  <Zap className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Design and Development Credits
                  </h3>
                  <p className="text-blue-700 font-medium text-xl mb-1">
                    Wunli.com
                  </p>
                  <p className="text-gray-600">
                    Digital Software Solutions
                  </p>
                  <p className="text-xs text-gray-500 mt-3">
                    This credit section cannot be removed or modified
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-8 border-t">
            <Button
              onClick={saveThemeSettings}
              disabled={loading}
              className="bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700 px-8"
            >
              {loading ? (
                "Saving..."
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Theme Settings
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ThemeCustomizer;